/*
  <sticky-variant-bar> — guided sticky add-to-basket bar.

  Walks the shopper through each option group (Pick your handle -> Pick your
  stand), then becomes Add to basket. Keys on option POSITION, never variant
  IDs, so it is reusable for any product with N options. Reads its steps from
  the data island the section renders; it drives Dawn's own variant-selects and
  clicks the real button[name="add"] — the single source of truth for what gets
  added. It never disables that button.

  Theme hooks (pinned so a theme bump is a known check — see constants.js):
    PUB_SUB_EVENTS.optionValueSelectionChange — fires on a user pick (global.js)
    PUB_SUB_EVENTS.variantChange              — fires on variant resolve (product-info.js)
*/
(function () {
  if (customElements.get('sticky-variant-bar')) return;

  // Reveal threshold used only when the buy box can't be observed directly.
  const SCROLL_FALLBACK_PX = 600;

  class StickyVariantBar extends HTMLElement {
    connectedCallback() {
      if (!this.realAdd) return; // no add button on the page (e.g. unavailable product); stay hidden
      this.steps = this.readSteps();
      this.total = this.steps.length;
      this.chosen = new Set();

      // The cascade exists to walk a shopper through options they have not
      // touched. Where the default selection is already the one most buyers
      // want, that reads as an obstacle: the bar says "Choose your handle" and
      // scrolls up over a form that is valid. start-ready marks every group
      // chosen at load, so the bar opens on Add to basket and the cascade only
      // ever runs where a product genuinely needs it.
      // Nothing to choose towards when every variant is sold out.
      if (this.dataset.startReady === 'true' || this.dataset.productAvailable === 'false') {
        this.steps.forEach((step) => this.chosen.add(step.position));
      }
      this.labelEl = this.querySelector('[data-svb-label]');
      this.cta = this.querySelector('[data-svb-cta]');
      this.addLabel = this.dataset.addLabel || 'Add to basket';

      this.cta.addEventListener('click', this.onCtaClick.bind(this));
      this.bindPicks();
      this.observeBuyBox();

      this.classList.add('svb-ready'); // CSS reveals only once upgraded; no-JS => bar stays absent
      this.render();
    }

    // --- reads -------------------------------------------------------------

    readSteps() {
      const island = this.querySelector('[data-svb-steps]');
      if (!island) return [];
      try {
        return JSON.parse(island.textContent);
      } catch (error) {
        throw new Error('sticky-variant-bar: steps data island is not valid JSON');
      }
    }

    get picker() {
      return document.querySelector('variant-selects');
    }

    get visSelector() {
      return document.querySelector('variant-image-selector');
    }

    get productInfo() {
      return document.querySelector('product-info');
    }

    get sectionId() {
      const info = this.productInfo;
      if (info && info.dataset.section) return info.dataset.section;
      return this.picker ? this.picker.dataset.section : null;
    }

    // The main product's add button, scoped to <product-info> so it is found on a
    // no-variant product (no <variant-selects>) and never picks up a
    // recommendation or pairs-well-with form button.
    get realAdd() {
      const info = this.productInfo;
      if (info) return info.querySelector('button[name="add"]');
      return document.querySelector('product-form button[name="add"]');
    }

    // Visible option groups in position order: the image tiles on this PDP, the
    // native fieldsets on a generic product. Queried live because Dawn rebuilds
    // variant-selects on a variant change.
    visAxes() {
      const vis = this.visSelector;
      return vis ? Array.from(vis.querySelectorAll('.vis__axis')) : [];
    }

    nativeGroups() {
      const picker = this.picker;
      return picker ? Array.from(picker.querySelectorAll('.product-form__input')) : [];
    }

    // --- picks feed the chosen set ----------------------------------------

    bindPicks() {
      const attach = () => {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onPick.bind(this));
        subscribe(PUB_SUB_EVENTS.variantChange, this.render.bind(this));
      };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') attach();
      else document.addEventListener('DOMContentLoaded', attach);

      // Re-selecting the already-selected default does NOT re-fire
      // optionValueSelectionChange (Dawn's radio is already checked), so a
      // shopper who wants the default handle could never advance the cascade.
      // Counting a direct tap on the control closes that gap; it only fires on a
      // genuine interaction, so the load-state default is never pre-populated.
      document.addEventListener('click', this.onPickerTap.bind(this));
    }

    onPick(event) {
      const target = event && event.data && event.data.target;
      const group = target && target.closest ? target.closest('.product-form__input') : null;
      this.markChosen(this.positionOfNativeGroup(group));
    }

    onPickerTap(event) {
      this.markChosen(this.positionFromElement(event.target));
    }

    markChosen(position) {
      if (!position) return;
      this.chosen.add(position);
      this.render();
    }

    positionFromElement(element) {
      if (!element || !element.closest) return 0;
      const axis = element.closest('.vis__axis');
      if (axis) return this.visAxes().indexOf(axis) + 1;
      return this.positionOfNativeGroup(element.closest('.product-form__input'));
    }

    positionOfNativeGroup(group) {
      if (!group) return 0;
      return this.nativeGroups().indexOf(group) + 1;
    }

    // --- render ------------------------------------------------------------

    firstUnchosen() {
      return this.steps.find((step) => !this.chosen.has(step.position)) || null;
    }

    render() {
      this.applySuppression();
      const next = this.total > 0 ? this.firstUnchosen() : null;
      if (next) {
        this.setMode('scroll', next.label, false);
        return;
      }
      const add = this.realAdd;
      const soldOut = !!(add && add.disabled);
      this.setMode('atc', soldOut ? this.soldOutText() : this.addLabelWithPrice(), soldOut);
    }

    // "Add to basket · £56.00". Prices are formatted by Liquid and looked up by
    // the live variant id on the real product form, so the bar never formats
    // money itself and cannot drift from what the form would add.
    addLabelWithPrice() {
      const price = this.currentPrice();
      return price ? this.addLabel + ' · ' + price : this.addLabel;
    }

    currentPrice() {
      if (!this.prices) this.prices = this.readPrices();
      const input = document.querySelector('input[name="id"].product-variant-id, product-info input[name="id"]');
      const id = input && input.value;
      return id ? this.prices[id] : null;
    }

    readPrices() {
      const island = this.querySelector('[data-svb-prices]');
      if (!island) return {};
      try {
        return JSON.parse(island.textContent);
      } catch (error) {
        console.error('sticky-variant-bar: prices island is not valid JSON', error);
        return {};
      }
    }

    setMode(mode, label, disabled) {
      this.dataset.mode = mode;
      this.labelEl.textContent = label;
      this.cta.disabled = disabled;
    }

    soldOutText() {
      return (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
    }

    // Suppress the load-state "selected" highlight on any group the shopper has
    // not touched. Re-derived every render so it survives Dawn rebuilding
    // variant-selects, and preserves per-group state.
    applySuppression() {
      this.steps.forEach((step) => {
        const untouched = !this.chosen.has(step.position);
        this.groupsForPosition(step.position).forEach((group) => {
          if (untouched) group.setAttribute('data-svb-untouched', '');
          else group.removeAttribute('data-svb-untouched');
        });
      });
    }

    groupsForPosition(position) {
      const groups = [];
      const axis = this.visAxes()[position - 1];
      const native = this.nativeGroups()[position - 1];
      if (axis) groups.push(axis);
      if (native) groups.push(native);
      return groups;
    }

    // --- cta click ---------------------------------------------------------

    onCtaClick() {
      const next = this.total > 0 ? this.firstUnchosen() : null;
      if (next) this.scrollToStep(next.position);
      else if (this.realAdd) this.realAdd.click();
    }

    scrollToStep(position) {
      const group = this.visibleGroup(position);
      if (!group) return;
      group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.focusGroup(group);
    }

    visibleGroup(position) {
      return this.groupsForPosition(position).find((group) => group.offsetParent !== null) || null;
    }

    focusGroup(group) {
      const focusable = group.querySelector('button, input:not([type="hidden"]), select, [tabindex]');
      if (focusable) focusable.focus({ preventScroll: true });
    }

    // --- visibility --------------------------------------------------------

    observeBuyBox() {
      const buyBox = this.realAdd ? this.realAdd.closest('.product-form__buttons') : null;
      if (buyBox && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(this.onBuyBoxIntersect.bind(this), {
          rootMargin: '0px 0px -40px 0px',
        });
        observer.observe(buyBox);
        return;
      }
      const onScroll = () => this.reveal(window.scrollY > SCROLL_FALLBACK_PX);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    // Reveal once the buy box has scrolled ABOVE the viewport (the shopper has
    // passed it), not merely whenever it is off-screen — otherwise a buy box
    // below the fold on load would show the bar at the very top.
    onBuyBoxIntersect(entries) {
      const entry = entries[0];
      this.reveal(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }

    reveal(show) {
      this.classList.toggle('is-revealed', show);
    }
  }

  customElements.define('sticky-variant-bar', StickyVariantBar);
})();
